import { useCallback, useEffect, useRef, useState } from 'react'

import {
  FinalResponseEvent,
  MessageService,
  StreamChunkEvent,
  StreamEndEvent,
  StreamStartEvent,
  WebSocketService,
  WebSocketState,
} from '../core'
import {
  createStreamChunkBuffer,
  createTextAnimator,
  getStableMarkdownLength,
  StreamChunkBuffer,
  TextAnimator,
} from '../core/utils/streaming'
import { parseAgentStatusEvent } from '../core/utils/parse-agent-status-event'
import { logUnhandledRealtimeFrame } from '../core/utils/realtime-diagnostics'
import useMaxChatStore from '../stores/use-max-chat-store'
import useMaxTurnStore from '../stores/use-max-turn-store'
import {
  MaxConnectionState,
  MaxFinalResponsePayload,
  MaxOutgoingSendMessagePayload,
  MaxTurnState,
} from '../types'
import { ChartConfig, FlowMetadata } from '../core/types/chat.types'
import useMaxFlowStore from '../stores/use-max-flow-store'
import { normalizeFlowMetadata } from '../utils/flow'
import { maxDebug, maxMessageDebug } from '../utils/max-debug'
import { FrameScope } from '../utils/turns/resolve-frame-scope'

const sharedWebSocketService = new WebSocketService()

type UseMaxRealtimeParams = {
  onRefreshConversations: () => Promise<void> | void
}

type StreamBufferState = {
  buffer: StreamChunkBuffer
  animator: TextAnimator
  messageId: string
  clientRequestId: string
  conversationId: string
  tenantId?: string
  tempConversationId?: string
  displayedContent: string
  animatingContent: string
}

type ResolvedFrame = {
  scope: FrameScope
  clientRequestId?: string
  turn?: MaxTurnState
}

const getStore = () => useMaxChatStore.getState()
const getTurnStore = () => useMaxTurnStore.getState()
const getFlowStore = () => useMaxFlowStore.getState()

const getFrameObject = (data: unknown): Record<string, unknown> | null => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  return data as Record<string, unknown>
}

const getTrimmedString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined

const getClientRequestId = (data: Record<string, unknown>): string | undefined =>
  getTrimmedString(data.clientRequestId)

const getTenantId = (data: Record<string, unknown>): string | undefined =>
  getTrimmedString(data.tenantId)

const getTempConversationId = (data: Record<string, unknown>): string | undefined =>
  getTrimmedString(data.tempConversationId)

const getConversationId = (data: Record<string, unknown>): string | undefined =>
  getTrimmedString(data.conversationId)

const readIsFlow = (payload: Record<string, unknown>): boolean =>
  typeof payload.is_flow === 'boolean'
    ? payload.is_flow
    : typeof payload.isFlow === 'boolean'
    ? payload.isFlow
    : false

const readFlowMetadata = (payload: Record<string, unknown>): FlowMetadata | undefined =>
  normalizeFlowMetadata(
    (payload.flow_metadata || payload.flowMetadata) as FlowMetadata | undefined
  ) || undefined

const parseMaxFinalResponse = (raw: unknown): MaxFinalResponsePayload | null => {
  if (!raw || typeof raw !== 'object') return null

  const asRecord = raw as Record<string, unknown>
  if (typeof asRecord.response === 'string' && typeof asRecord.conversationId === 'string') {
    return {
      requestId: typeof asRecord.requestId === 'string' ? asRecord.requestId : undefined,
      conversationId: asRecord.conversationId,
      response: asRecord.response,
      messageId: typeof asRecord.messageId === 'string' ? asRecord.messageId : undefined,
      tempConversationId:
        typeof asRecord.tempConversationId === 'string' ? asRecord.tempConversationId : undefined,
      timestamp: typeof asRecord.timestamp === 'string' ? asRecord.timestamp : undefined,
      responseType:
        typeof asRecord.type === 'string'
          ? asRecord.type
          : typeof asRecord.responseType === 'string'
          ? asRecord.responseType
          : undefined,
      isFlow: readIsFlow(asRecord),
      flowMetadata: readFlowMetadata(asRecord),
      graphs: asRecord.graphs as ChartConfig[] | undefined,
    }
  }

  if (asRecord.type !== 'message:received' || !asRecord.data || typeof asRecord.data !== 'object') {
    return null
  }

  return parseMaxFinalResponse(asRecord.data)
}

const toEpoch = (value?: Date | string): number | undefined => {
  if (!value) return undefined
  if (value instanceof Date) return value.getTime()
  return new Date(value).getTime()
}

const findConversationSummary = (conversationId: string | null | undefined) => {
  if (!conversationId) return undefined
  const state = getStore()
  return [...state.pinnedConversations, ...state.conversations].find(
    (conversation) => conversation.id === conversationId
  )
}

/** Updates the sidebar preview of the real conversation, or of its temporary entry while new. */
const updateConversationPreview = (
  conversationIds: Array<string | null | undefined>,
  preview: string
) => {
  const conversationId = conversationIds.find((id) => Boolean(findConversationSummary(id)))
  if (!conversationId) return

  getStore().updateConversationLocal({
    conversationId,
    preview: preview.slice(0, 100),
    updatedAt: Date.now(),
  })
}

/**
 * Frame scope (resolveFrameScope): every frame of a registered turn updates the turn store, the
 * trails and the stream sessions; the scope only decides what reaches the view and the sidebar.
 */
const resolveFrame = (source: string, data: Record<string, unknown> | null): ResolvedFrame => {
  if (!data) {
    logUnhandledRealtimeFrame(source, 'payload is not an object', data)
    return { scope: 'unknown' }
  }

  const store = getStore()
  const turnStore = getTurnStore()
  const clientRequestId = getClientRequestId(data)
  const scope = store.resolveFrameScope({
    clientRequestId,
    tenantId: getTenantId(data),
  })
  if (scope === 'unknown') {
    const reason = !clientRequestId
      ? 'missing clientRequestId'
      : !turnStore.get(clientRequestId)
      ? 'unknown clientRequestId'
      : 'inconsistent frame envelope'
    logUnhandledRealtimeFrame(source, reason, data)
    return { scope, clientRequestId }
  }

  const turn = turnStore.get(clientRequestId)
  const frameConversationId = getConversationId(data)
  // The request_received status of a new conversation carries the temporary id as conversationId.
  if (
    clientRequestId &&
    turn &&
    frameConversationId &&
    frameConversationId !== turn.tempConversationId
  ) {
    turnStore.bindConversation(clientRequestId, frameConversationId)
  }
  const boundTurn = getTurnStore().get(clientRequestId)
  const activityScope = boundTurn?.conversationId || boundTurn?.tempConversationId
  if (activityScope) store.noteTurnActivity(activityScope)
  return { scope, clientRequestId, turn: boundTurn }
}

const useMaxRealtime = ({ onRefreshConversations }: UseMaxRealtimeParams) => {
  const refreshConversationsRef = useRef(onRefreshConversations)
  const subscriptionsRef = useRef<Array<() => void>>([])
  const streamBuffersRef = useRef<Map<string, StreamBufferState>>(new Map())
  const messageServiceRef = useRef(new MessageService())

  const [connectionState, setConnectionState] = useState<MaxConnectionState>(
    sharedWebSocketService.getState()
  )

  useEffect(() => {
    refreshConversationsRef.current = onRefreshConversations
  }, [onRefreshConversations])

  const isConnected = connectionState === WebSocketState.CONNECTED

  const clearStreamBuffers = useCallback(() => {
    if (streamBuffersRef.current.size > 0) {
      maxDebug.warn('Realtime clearing stream buffers', {
        activeStreams: streamBuffersRef.current.size,
      })
    }
    const states = Array.from(streamBuffersRef.current.values())
    streamBuffersRef.current.clear()
    states.forEach((state) => state.animator.cancel())
  }, [])

  const createStreamBufferState = useCallback(
    (params: {
      requestId: string
      messageId: string
      clientRequestId: string
      conversationId: string
      tenantId?: string
      tempConversationId?: string
    }): StreamBufferState => {
      const state: StreamBufferState = {
        buffer: createStreamChunkBuffer({
          minChunks: 10,
          maxChunks: 20,
          flushTimeoutMs: 1000,
          enableTableAwareFlush: true,
        }),
        animator: null as unknown as TextAnimator,
        messageId: params.messageId,
        clientRequestId: params.clientRequestId,
        conversationId: params.conversationId,
        tenantId: params.tenantId,
        tempConversationId: params.tempConversationId,
        displayedContent: '',
        animatingContent: '',
      }
      state.animator = createTextAnimator({
        wordsPerSecond: 30,
        getStableLength: getStableMarkdownLength,
        onUpdate: (visibleText) => {
          const current = streamBuffersRef.current.get(params.requestId)
          if (!current) return
          current.animatingContent = visibleText
          getTurnStore().setVisibleContent(current.clientRequestId, visibleText)
          getStore().syncTurnToView(current.clientRequestId)
        },
      })
      return state
    },
    []
  )

  const setupEventHandlers = useCallback(() => {
    if (subscriptionsRef.current.length > 0) {
      maxDebug.debug('Realtime subscriptions already installed', {
        subscriptionCount: subscriptionsRef.current.length,
      })
      return
    }

    const wsService = sharedWebSocketService
    const messageService = messageServiceRef.current

    const unsubAgentStatus = wsService.subscribe('agent_status', (event) => {
      const frame = getFrameObject(event.data)
      const scopeResult = resolveFrame('useMaxRealtime.agent_status', frame)
      if (scopeResult.scope === 'unknown') return

      const payload = parseAgentStatusEvent(frame)
      if (!payload) {
        logUnhandledRealtimeFrame(
          'useMaxRealtime.agent_status',
          'frame failed validation (requires requestId, conversationId and a non-empty label)',
          event.data
        )
        return
      }
      getStore().ingestAgentStatus(payload)
    })
    subscriptionsRef.current.push(unsubAgentStatus)

    const unsubStreamStart = wsService.subscribe('stream_start', (event) => {
      const payload = getFrameObject(event.data)
      const scopeResult = resolveFrame('useMaxRealtime.stream_start', payload)
      if (scopeResult.scope === 'unknown') return
      if (!payload?.requestId || !payload.conversationId) {
        logUnhandledRealtimeFrame(
          'useMaxRealtime.stream_start',
          'missing requestId or conversationId',
          event.data
        )
        return
      }

      const clientRequestId = scopeResult.clientRequestId!
      const turnStore = getTurnStore()
      const startPayload: StreamStartEvent = {
        requestId: payload.requestId as string,
        clientRequestId,
        tenantId: getTenantId(payload),
        tempConversationId: getTempConversationId(payload),
        messageId: payload.messageId as string | undefined,
        conversationId: payload.conversationId as string,
        role: payload.role as string | undefined,
        timestamp: payload.timestamp as string | undefined,
        responseType: payload.type as string | undefined,
        isFlow: readIsFlow(payload),
        flowMetadata: readFlowMetadata(payload),
        graphs: payload.graphs as ChartConfig[] | undefined,
      }

      turnStore.bindRequest(clientRequestId, startPayload.requestId)
      turnStore.setAssistantMessageId(clientRequestId, startPayload.messageId || '')

      if (getStore().consumeVisualizationPending(startPayload.conversationId)) {
        startPayload.responseType = 'VISUALIZE'
      }

      turnStore.setStreamMetadata(clientRequestId, {
        isFlow: startPayload.isFlow,
        flowMetadata: startPayload.flowMetadata,
        responseType: startPayload.responseType,
      })

      // D-29: stream_start no longer creates the assistant message; the first non-blank flush
      // does. Every write for the turn targets the id resolved here (A-21).
      const result = messageService.startStream(startPayload)
      turnStore.setAssistantMessageId(clientRequestId, result.messageId)
      getStore().bindAgentTrailMessage({
        requestId: result.requestId,
        messageId: result.messageId,
        conversationId: result.conversationId,
      })
      if (scopeResult.scope === 'visible') {
        getStore().setTyping(true)
      }

      maxMessageDebug.event('Realtime stream_start received', {
        requestId: result.requestId,
        clientRequestId,
        messageId: result.messageId,
        conversationId: result.conversationId,
        scope: scopeResult.scope,
        responseType: result.responseType || null,
      })

      streamBuffersRef.current.set(
        startPayload.requestId,
        createStreamBufferState({
          requestId: startPayload.requestId,
          messageId: result.messageId,
          clientRequestId,
          conversationId: result.conversationId,
          tenantId: startPayload.tenantId,
          tempConversationId: startPayload.tempConversationId,
        })
      )
    })
    subscriptionsRef.current.push(unsubStreamStart)

    const unsubStreamChunk = wsService.subscribe('chunk', (event) => {
      const payload = getFrameObject(event.data)
      const scopeResult = resolveFrame('useMaxRealtime.chunk', payload)
      if (scopeResult.scope === 'unknown') return
      if (!payload?.requestId || payload.content === undefined || payload.index === undefined) {
        logUnhandledRealtimeFrame(
          'useMaxRealtime.chunk',
          'missing requestId, content or index',
          event.data
        )
        return
      }

      const clientRequestId = scopeResult.clientRequestId!
      const turnStore = getTurnStore()
      const chunkPayload: StreamChunkEvent = {
        requestId: payload.requestId as string,
        clientRequestId,
        tenantId: getTenantId(payload),
        tempConversationId: getTempConversationId(payload),
        conversationId: payload.conversationId as string | undefined,
        messageId: payload.messageId as string | undefined,
        role: payload.role as string | undefined,
        type: payload.type as string | undefined,
        content: String(payload.content),
        index: Number(payload.index),
        contentBlockIndex: payload.contentBlockIndex as number | undefined,
        timestamp: payload.timestamp as string | undefined,
        responseType: payload.type as string | undefined,
        isFlow: readIsFlow(payload),
        flowMetadata: readFlowMetadata(payload),
        graphs: payload.graphs as ChartConfig[] | undefined,
      }

      turnStore.bindRequest(clientRequestId, chunkPayload.requestId)
      const turn = turnStore.get(clientRequestId)
      const update = messageService.appendStreamChunk(chunkPayload)
      if (!update) return
      if (!turn?.assistantMessageId) {
        turnStore.setAssistantMessageId(clientRequestId, update.messageId)
      }

      const requestId = chunkPayload.requestId
      let streamState = streamBuffersRef.current.get(requestId)
      if (!streamState) {
        maxMessageDebug.event('Realtime chunk created fallback stream buffer', {
          requestId,
          clientRequestId,
          conversationId: update.conversationId,
        })
        streamState = createStreamBufferState({
          requestId,
          messageId: update.messageId,
          clientRequestId,
          conversationId: update.conversationId,
          tenantId: chunkPayload.tenantId,
          tempConversationId: chunkPayload.tempConversationId,
        })
        streamBuffersRef.current.set(requestId, streamState)
      }

      const flushResult = streamState.buffer.push(chunkPayload.content)
      if (!flushResult) return

      streamState.displayedContent += flushResult.text
      const store = getStore()
      const currentScope = store.resolveFrameScope({
        clientRequestId,
        tenantId: chunkPayload.tenantId,
      })
      if (currentScope === 'unknown') return

      // First visible answer content closes the reasoning line (C-1).
      store.closeAgentTrail(chunkPayload.requestId, 'content')
      if (currentScope === 'visible') {
        store.setTyping(false)
      }
      streamState.animator.animate(flushResult.text)
      if (currentScope === 'visible' || currentScope === 'background') {
        updateConversationPreview(
          [update.conversationId, streamState.tempConversationId],
          streamState.displayedContent
        )
      }
    })
    subscriptionsRef.current.push(unsubStreamChunk)

    const unsubStreamEnd = wsService.subscribe('stream_end', (event) => {
      const payload = getFrameObject(event.data)
      const scopeResult = resolveFrame('useMaxRealtime.stream_end', payload)
      if (scopeResult.scope === 'unknown') return
      if (!payload?.requestId) {
        logUnhandledRealtimeFrame('useMaxRealtime.stream_end', 'missing requestId', event.data)
        return
      }

      const clientRequestId = scopeResult.clientRequestId!
      const turnStore = getTurnStore()
      const endPayload: StreamEndEvent = {
        requestId: payload.requestId as string,
        clientRequestId,
        tenantId: getTenantId(payload),
        tempConversationId: getTempConversationId(payload),
        conversationId: payload.conversationId as string | undefined,
        messageId: payload.messageId as string | undefined,
        role: payload.role as string | undefined,
        type: payload.type as string | undefined,
        stopReason: payload.stopReason as string | undefined,
        timestamp: payload.timestamp as string | undefined,
        responseType: payload.type as string | undefined,
        isFlow: readIsFlow(payload),
        flowMetadata: readFlowMetadata(payload),
        graphs: payload.graphs as ChartConfig[] | undefined,
      }

      const requestId = endPayload.requestId
      const streamState = streamBuffersRef.current.get(requestId)
      const turn = turnStore.get(clientRequestId)
      if (endPayload.messageId && !turn?.assistantMessageId) {
        turnStore.setAssistantMessageId(clientRequestId, endPayload.messageId)
      }

      const store = getStore()
      const currentScope = store.resolveFrameScope({
        clientRequestId,
        tenantId: endPayload.tenantId,
      })
      if (currentScope === 'unknown') return

      let displayedContent = turn?.final?.content ?? turn?.visibleContent ?? ''
      if (streamState) {
        const finalFlush = streamState.buffer.flush('stream_end')
        if (finalFlush) streamState.displayedContent += finalFlush.text

        streamState.animator.cancel()
        displayedContent = streamState.displayedContent
      }

      maxMessageDebug.event('Realtime stream_end received', {
        requestId,
        clientRequestId,
        scope: currentScope,
        hadBuffer: Boolean(streamState),
      })

      turnStore.setVisibleContent(clientRequestId, displayedContent)
      turnStore.markStreamEnded(clientRequestId)
      store.syncTurnToView(clientRequestId)
      store.closeAgentTrail(endPayload.requestId, 'stream_end')
      if (currentScope === 'visible' || currentScope === 'background') {
        updateConversationPreview(
          [
            streamState?.conversationId ?? endPayload.conversationId,
            streamState?.tempConversationId,
          ],
          displayedContent
        )
      }
      if (currentScope === 'visible') store.setTyping(false)
      streamBuffersRef.current.delete(requestId)

      messageService.endStream(endPayload)
    })
    subscriptionsRef.current.push(unsubStreamEnd)

    const unsubMessageReceived = wsService.subscribe('message:received', (event) => {
      const messageData = getFrameObject(event.data)
      const scopeResult = resolveFrame('useMaxRealtime.message:received', messageData)
      const clientRequestId = scopeResult.clientRequestId
      const parsed = parseMaxFinalResponse(messageData)

      if (!parsed) {
        if (scopeResult.scope !== 'unknown') {
          logUnhandledRealtimeFrame(
            'useMaxRealtime.message:received',
            'frame matches neither the final-response shape nor the legacy shape',
            event.data
          )
        }
        return
      }

      if (scopeResult.scope === 'unknown') return

      if (!parsed.requestId) {
        logUnhandledRealtimeFrame(
          'useMaxRealtime.message:received',
          'missing requestId',
          event.data
        )
        return
      }

      const finalPayload: FinalResponseEvent = {
        requestId: parsed.requestId,
        clientRequestId,
        tenantId: messageData ? getTenantId(messageData) : undefined,
        conversationId: parsed.conversationId,
        response: parsed.response,
        messageId: parsed.messageId,
        tempConversationId: parsed.tempConversationId,
        timestamp: parsed.timestamp,
        responseType: parsed.responseType,
        isFlow: parsed.isFlow,
        flowMetadata: parsed.flowMetadata,
        rawPayload: messageData || undefined,
        graphs: parsed.graphs,
      }

      if (getStore().consumeVisualizationPending(finalPayload.conversationId)) {
        finalPayload.responseType = 'VISUALIZE'
      }

      const finalResult = messageService.finalizeStreamResponse(finalPayload)
      const convId = finalResult.conversationId
      const turn = scopeResult.turn
      const tempId = finalResult.tempConversationId || turn?.tempConversationId || undefined
      const turnStore = getTurnStore()

      maxMessageDebug.event('Realtime final response received', {
        requestId: finalResult.requestId,
        clientRequestId,
        messageId: finalResult.messageId,
        conversationId: convId,
        tempConversationId: tempId || null,
        scope: scopeResult.scope,
        responseLength: finalResult.content.length,
      })

      if (clientRequestId) {
        turnStore.bindConversation(clientRequestId, convId)
        turnStore.setAssistantMessageId(clientRequestId, finalResult.messageId)
        turnStore.markFinal(clientRequestId, {
          content: finalResult.content,
          createdAt: toEpoch(finalResult.createdAt) ?? Date.now(),
          isFlow: finalResult.isFlow,
          flowMetadata: finalResult.flowMetadata,
          responseType: finalResult.responseType,
          graphs: finalResult.graphs,
        })
      }

      const streamState = streamBuffersRef.current.get(finalResult.requestId)
      if (streamState) {
        streamBuffersRef.current.delete(finalResult.requestId)
        streamState.animator.cancel()
      }

      const store = getStore()
      store.bindAgentTrailMessage({
        requestId: finalResult.requestId,
        messageId: finalResult.messageId,
        conversationId: convId,
      })
      store.closeAgentTrail(finalResult.requestId, 'final')
      messageService.discardStream(finalResult.requestId)

      if (scopeResult.scope !== 'foreign' && finalResult.flowMetadata) {
        getFlowStore().setFlowMetadata(convId, finalResult.flowMetadata)
      }

      const isVisibleTempConversation = Boolean(
        scopeResult.scope === 'visible' &&
          tempId &&
          turn?.tempConversationId === tempId &&
          store.tempConversationId === tempId
      )
      if (isVisibleTempConversation && tempId) {
        store.migrateTempConversation({
          tempConversationId: tempId,
          realConversationId: convId,
        })
        getFlowStore().migrateConversationState(tempId, convId)
      }

      if (scopeResult.scope === 'visible') {
        getStore().completeStreamingMessage({
          requestId: finalResult.requestId,
          messageId: finalResult.messageId,
          conversationId: convId,
          content: finalResult.content,
          createdAt: toEpoch(finalResult.createdAt),
          responseType: finalResult.responseType,
          isFlow: finalResult.isFlow,
          flowMetadata: finalResult.flowMetadata,
          graphs: finalResult.graphs,
          tempConversationId: tempId,
          scope: scopeResult.scope,
        })
      } else {
        store.stopTurnWatchdog(convId)
        if (tempId) store.stopTurnWatchdog(tempId)
      }

      // A new conversation that finished off screen is not migrated nor navigated to; its
      // temporary entry gives way to the real conversation in the sidebar.
      const temporarySummary = tempId ? findConversationSummary(tempId) : undefined
      if (scopeResult.scope === 'background' && tempId) {
        getStore().discardTempConversation(tempId)
      }

      if (
        (scopeResult.scope === 'visible' || scopeResult.scope === 'background') &&
        convId !== 'current-conversation'
      ) {
        const stateAfter = getStore()
        if (findConversationSummary(convId)) {
          stateAfter.updateConversationLocal({
            conversationId: convId,
            preview: finalResult.content.slice(0, 100),
            updatedAt: Date.now(),
          })
        } else {
          const sourceMessages = stateAfter.messagesByConversation[convId] || []
          const firstUserMessage = sourceMessages.find((message) => message.role === 'user')
          stateAfter.upsertConversation({
            id: convId,
            title:
              firstUserMessage?.content?.slice(0, 50) ||
              temporarySummary?.title ||
              'New Conversation',
            preview: finalResult.content.slice(0, 100),
            updatedAt: Date.now(),
          })
        }
      }

      if (scopeResult.scope === 'visible') {
        const stateAfter = getStore()
        if (
          stateAfter.awaitingConversationId === convId ||
          (tempId && stateAfter.awaitingConversationId === tempId)
        ) {
          stateAfter.setAwaitingConversationId(null)
        }
        stateAfter.setTransportError(null)
      }
    })
    subscriptionsRef.current.push(unsubMessageReceived)

    const unsubConvCreated = wsService.subscribe('conversation:created', () => {
      maxDebug.warn('Realtime conversation:created received; refreshing conversations')
      void Promise.resolve(refreshConversationsRef.current()).catch((error) => {
        maxDebug.error('Realtime conversation:created refresh failed', {
          errorMessage: error instanceof Error ? error.message : String(error),
        })
      })
    })
    subscriptionsRef.current.push(unsubConvCreated)

    const unsubConvUpdated = wsService.subscribe('conversation:updated', () => {
      maxDebug.warn('Realtime conversation:updated received; refreshing conversations')
      void Promise.resolve(refreshConversationsRef.current()).catch((error) => {
        maxDebug.error('Realtime conversation:updated refresh failed', {
          errorMessage: error instanceof Error ? error.message : String(error),
        })
      })
    })
    subscriptionsRef.current.push(unsubConvUpdated)

    const unsubConvDeleted = wsService.subscribe('conversation:deleted', () => {
      maxDebug.warn('Realtime conversation:deleted received; refreshing conversations')
      void Promise.resolve(refreshConversationsRef.current()).catch((error) => {
        maxDebug.error('Realtime conversation:deleted refresh failed', {
          errorMessage: error instanceof Error ? error.message : String(error),
        })
      })
    })
    subscriptionsRef.current.push(unsubConvDeleted)

    const unsubTypingStart = wsService.subscribe('typing:start', (event) => {
      const typingData = getFrameObject(event.data)
      const scopeResult = resolveFrame('useMaxRealtime.typing:start', typingData)
      if (scopeResult.scope === 'visible') getStore().setTyping(true)
    })
    subscriptionsRef.current.push(unsubTypingStart)

    const unsubTypingStop = wsService.subscribe('typing:stop', (event) => {
      const scopeResult = resolveFrame('useMaxRealtime.typing:stop', getFrameObject(event.data))
      if (scopeResult.scope === 'visible') getStore().setTyping(false)
    })
    subscriptionsRef.current.push(unsubTypingStop)

    const unsubError = wsService.subscribe('error', (event) => {
      const errorData = getFrameObject(event.data)
      maxMessageDebug.error('Realtime websocket error event received', {
        dataType: typeof event.data,
        connectionState: wsService.getState(),
      })
      const scopeResult = resolveFrame('useMaxRealtime.error', errorData)
      if (scopeResult.scope === 'visible' && typeof errorData?.message === 'string') {
        getStore().setTransportError(errorData.message)
      }
    })
    subscriptionsRef.current.push(unsubError)

    const unsubConnected = wsService.subscribe('connected', () => {
      setConnectionState(WebSocketState.CONNECTED)
    })
    subscriptionsRef.current.push(unsubConnected)

    const unsubDisconnected = wsService.subscribe('disconnected', () => {
      maxMessageDebug.event('Realtime websocket disconnected event received', {
        nextState: wsService.getState(),
      })
      setConnectionState(wsService.getState())
    })
    subscriptionsRef.current.push(unsubDisconnected)
  }, [createStreamBufferState])

  const connect = useCallback(
    async (url: string, token?: string): Promise<void> => {
      setupEventHandlers()
      setConnectionState(
        sharedWebSocketService.getState() === WebSocketState.RECONNECTING
          ? WebSocketState.RECONNECTING
          : WebSocketState.CONNECTING
      )
      await sharedWebSocketService.connect(url, token)
      setConnectionState(sharedWebSocketService.getState())
    },
    [setupEventHandlers]
  )

  const disconnect = useCallback(() => {
    subscriptionsRef.current.forEach((unsubscribe) => unsubscribe())
    subscriptionsRef.current = []

    // Clean up stream buffers, service-side sessions and every turn-scoped piece of state.
    clearStreamBuffers()
    messageServiceRef.current.resetStreams()
    getTurnStore().clearAll()
    const store = getStore()
    store.stopPendingTurnPolling()
    store.stopAllTurnWatchdogs()
    store.clearAgentTrails()
    store.setTyping(false)
    store.setAwaitingConversationId(null)

    sharedWebSocketService.disconnect()
    setConnectionState(sharedWebSocketService.getState())
  }, [clearStreamBuffers])

  const sendMessage = useCallback((payload: MaxOutgoingSendMessagePayload): boolean => {
    if (!sharedWebSocketService.isConnected()) {
      maxMessageDebug.error('Realtime send blocked: websocket not connected', {
        state: sharedWebSocketService.getState(),
        conversationId: payload.conversationId,
        tempConversationId: payload.tempConversationId,
        mode: payload.mode,
        promptLength: payload.prompt.length,
      })
      getStore().setTransportError(
        `Cannot send message: WebSocket is ${sharedWebSocketService.getState()}. Please wait for reconnection.`
      )
      return false
    }

    maxMessageDebug.event('Realtime send emitting message:sent', {
      conversationId: payload.conversationId,
      tempConversationId: payload.tempConversationId,
      clientRequestId: payload.clientRequestId,
      tenantId: payload.tenantId,
      mode: payload.mode,
      isFlow: Boolean(payload.is_flow),
      promptLength: payload.prompt.length,
    })
    sharedWebSocketService.emit('message:sent', payload, payload.conversationId)
    return true
  }, [])

  return {
    isConnected,
    connectionState,
    connect,
    disconnect,
    reconnect: connect,
    sendMessage,
  }
}

export default useMaxRealtime
